import React, { useState, useEffect, useContext, useMemo } from "react";
import {
  Briefcase,
  Building2,
  Copy,
  Plus,
  RefreshCw,
  Trash2,
} from "lucide-react";
import { FieldConfigurationContext } from "../../../../../lib/context/FieldConfigurationContext";
import SearchBar from "../../components/SearchBar";
import SortableTable from "../../components/SortableTable";
import PaginationControls from "../../components/PaginationControls";
import ExportButton from "../../components/ExportButton";
import Button from "../../../../common/Button";
import DeleteApprovalTeamModal from "./DeleteApprovalTeamModal";
import CopyApprovalTeamModal from "./CopyApprovalTeamModal";
import { getActiveCompanyOptions } from "../AccessGroupsList";
import { SELECTED_COMPANY_KEY } from "../../../../../lib/local/SelectedCompany";
import "../../../../common/SelectInput/style.css";
import "../../style.css";
import "../RolesList.css";

export const SELECTED_COMPANY_STORAGE_KEY = SELECTED_COMPANY_KEY;

function formatMemberNames(companyUsers) {
  return (companyUsers || [])
    .map((u) => `${u.first_name || ""} ${u.last_name || ""}`.trim())
    .filter(Boolean)
    .join(", ");
}

export default function ApprovalTeamsList({
  onTeamSelect,
  onAddTeam,
  onJobAssignments,
}) {
  const {
    approvalTeams,
    fetchApprovalTeams,
    createApprovalTeam,
    deleteApprovalTeam,
    companies,
  } = useContext(FieldConfigurationContext);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [paginationConfig, setPaginationConfig] = useState({
    currentPage: 1,
    pageSize: 10,
  });
  const [deleteTeam, setDeleteTeam] = useState(null);
  const [copyTeam, setCopyTeam] = useState(null);
  const [copySuccessMessage, setCopySuccessMessage] = useState(null);
  const [selectedCompanyUuid, setSelectedCompanyUuid] = useState("");

  useEffect(() => {
    fetchApprovalTeams();
  }, [fetchApprovalTeams]);

  const companyOptions = useMemo(
    () =>
      getActiveCompanyOptions(companies, companies.configureApprovalGroupsData),
    [
      companies.data,
      companies.codesMap,
      companies.enabledData,
      companies.configureApprovalGroupsData,
    ]
  );

  // Default the company selection to the sticky (last-used) company if it's
  // still available, otherwise the first company in the list. The dropdown
  // is always shown (even with a single option) rather than swapping in a
  // Company column when there's only one.
  useEffect(() => {
    if (companyOptions.length === 0) return;
    if (
      selectedCompanyUuid &&
      companyOptions.some((opt) => opt.value === selectedCompanyUuid)
    ) {
      return;
    }
    const stored = localStorage.getItem(SELECTED_COMPANY_STORAGE_KEY);
    const initial =
      stored && companyOptions.some((opt) => opt.value === stored)
        ? stored
        : companyOptions[0].value;
    setSelectedCompanyUuid(initial);
  }, [companyOptions, selectedCompanyUuid]);

  const handleCompanyChange = (e) => {
    const uuid = e.target.value;
    setSelectedCompanyUuid(uuid);
    localStorage.setItem(SELECTED_COMPANY_STORAGE_KEY, uuid);
  };

  const filteredTeams = useMemo(() => {
    let data = approvalTeams.data;

    if (selectedCompanyUuid) {
      data = data.filter((team) => team.company_uuid === selectedCompanyUuid);
    }

    if (!searchTerm) return data;

    const searchLower = searchTerm.toLowerCase();
    return data.filter((team) => {
      const memberNames = formatMemberNames(team.member_info).toLowerCase();
      return (
        team.name?.toLowerCase().includes(searchLower) ||
        memberNames.includes(searchLower)
      );
    });
  }, [approvalTeams.data, searchTerm, selectedCompanyUuid]);

  const sortedTeams = useMemo(() => {
    if (!sortConfig.key) return filteredTeams;

    return [...filteredTeams].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      if (sortConfig.key === "member_count") {
        aValue = a.member_count || 0;
        bValue = b.member_count || 0;
        return sortConfig.direction === "asc"
          ? aValue - bValue
          : bValue - aValue;
      } else if (sortConfig.key === "member_names") {
        aValue = formatMemberNames(a.member_info);
        bValue = formatMemberNames(b.member_info);
      } else if (sortConfig.key === "created_at") {
        aValue = a.created_at ? new Date(a.created_at).getTime() : 0;
        bValue = b.created_at ? new Date(b.created_at).getTime() : 0;
        return sortConfig.direction === "asc"
          ? aValue - bValue
          : bValue - aValue;
      }

      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      if (typeof aValue === "string") {
        return sortConfig.direction === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return 0;
    });
  }, [filteredTeams, sortConfig]);

  const paginatedTeams = useMemo(() => {
    const startIndex =
      (paginationConfig.currentPage - 1) * paginationConfig.pageSize;
    return sortedTeams.slice(
      startIndex,
      startIndex + paginationConfig.pageSize
    );
  }, [sortedTeams, paginationConfig]);

  useEffect(() => {
    setPaginationConfig((prev) => ({ ...prev, currentPage: 1 }));
  }, [filteredTeams.length]);

  const handleSort = (columnKey) => {
    setSortConfig((prev) => ({
      key: columnKey,
      direction:
        prev.key === columnKey && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const handleDeleteClick = (team, event) => {
    event.stopPropagation();
    setDeleteTeam(team);
  };

  const handleDeleted = () => {
    setDeleteTeam(null);
  };

  const handleCopyClick = (team, event) => {
    event.stopPropagation();
    setCopySuccessMessage(null);
    setCopyTeam(team);
  };

  const handleCopied = (newTeam, targetCompanyName, removedCount) => {
    setCopyTeam(null);
    const removedNote =
      removedCount > 0
        ? ` ${removedCount} member${
            removedCount === 1 ? " was" : "s were"
          } left out because they aren't available in that company.`
        : "";
    setCopySuccessMessage(
      `Copied "${newTeam?.name}" to ${targetCompanyName}.${removedNote}`
    );
  };

  const columns = [
    {
      key: "name",
      label: "Name",
      sortable: true,
      width: "200px",
      render: (team, value) => (
        <span
          style={{
            fontWeight: 500,
            color: "rgba(46, 50, 66, 1)",
            display: "block",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: "200px",
          }}
          title={value || ""}
        >
          {value}
        </span>
      ),
    },
    {
      key: "member_count",
      label: "Members",
      sortable: true,
      width: "100px",
      render: (team) => (
        <span style={{ color: "rgba(100, 116, 139, 1)" }}>
          {team.member_count || 0}
        </span>
      ),
    },
    {
      key: "member_names",
      label: "Member Names",
      sortable: true,
      width: "400px",
      render: (team) => {
        const names = formatMemberNames(team.member_info);
        return (
          <span
            style={{
              color: "rgba(100, 116, 139, 1)",
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "400px",
            }}
            title={names}
          >
            {names || "—"}
          </span>
        );
      },
    },
    {
      key: "created_at",
      label: "Created",
      sortable: true,
      width: "120px",
      render: (team) => (
        <span style={{ color: "rgba(100, 116, 139, 1)" }}>
          {team.created_at
            ? new Date(team.created_at).toLocaleDateString()
            : "—"}
        </span>
      ),
    },
  ];

  const exportColumns = [
    { key: "name", label: "Name" },
    {
      key: "member_count",
      label: "Members",
      format: (val) => val || 0,
    },
    {
      key: "member_info",
      label: "Member Names",
      format: (val) => formatMemberNames(val),
    },
    {
      key: "created_at",
      label: "Created",
      format: (val) => (val ? new Date(val).toLocaleDateString() : ""),
    },
  ];

  const actions = [
    {
      label: "Copy",
      icon: Copy,
      onClick: handleCopyClick,
    },
    {
      label: "Delete",
      icon: Trash2,
      onClick: handleDeleteClick,
      className: "action-button-delete",
    },
  ];

  return (
    <div className="roles-list">
      <div className="roles-list__header">
        <div className="roles-list__header-top">
          <div className="roles-list__header-left">
            <h2 className="roles-list__title">Approval Teams</h2>
            {companyOptions.length > 0 && (
              <div className="roles-list__company-filter">
                <Building2
                  size={18}
                  style={{ color: "rgba(100, 116, 139, 1)", flexShrink: 0 }}
                />
                <select
                  className="roles-list__company-select"
                  value={selectedCompanyUuid}
                  onChange={handleCompanyChange}
                  title={
                    companyOptions.find(
                      (opt) => opt.value === selectedCompanyUuid
                    )?.label || ""
                  }
                >
                  {companyOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search approval teams..."
          />
          <div className="roles-list__actions">
            <ExportButton
              data={sortedTeams}
              filename="approval_teams_export"
              columns={exportColumns}
              className="button--secondary"
            />
            <Button
              className="button--secondary"
              onClick={() => fetchApprovalTeams(true)}
              disabled={approvalTeams.loading}
              title="Refresh approval teams"
            >
              <RefreshCw size={18} style={{ marginRight: "6px" }} />
              Refresh
            </Button>
            <Button className="button--secondary" onClick={onJobAssignments}>
              <Briefcase size={18} style={{ marginRight: "6px" }} />
              Job Assignments
            </Button>
            <Button className="button--primary" onClick={onAddTeam}>
              <Plus size={18} style={{ marginRight: "6px" }} />
              Add Approval Team
            </Button>
          </div>
        </div>
      </div>

      {copySuccessMessage && (
        <div
          style={{
            margin: "12px 0",
            padding: "10px 14px",
            background: "#f0fdf4",
            border: "1px solid #bbf7d0",
            borderRadius: "6px",
            color: "#166534",
            fontSize: "13px",
          }}
        >
          {copySuccessMessage}
        </div>
      )}

      {approvalTeams.loading && (
        <div className="roles-list__loading">
          <p>Loading approval teams...</p>
        </div>
      )}

      {approvalTeams.error && (
        <div className="roles-list__error">
          <p>Error loading approval teams: {approvalTeams.error}</p>
          <Button onClick={() => fetchApprovalTeams(true)}>Retry</Button>
        </div>
      )}

      {!approvalTeams.loading && !approvalTeams.error && (
        <>
          <SortableTable
            data={paginatedTeams}
            columns={columns}
            sortConfig={sortConfig}
            onSort={handleSort}
            onRowClick={onTeamSelect}
            actions={actions}
            emptyMessage="No approval teams found. Click 'Add Approval Team' to create your first team."
          />
          <PaginationControls
            currentPage={paginationConfig.currentPage}
            pageSize={paginationConfig.pageSize}
            totalItems={sortedTeams.length}
            onPageChange={(newPage) => {
              setPaginationConfig((prev) => ({
                ...prev,
                currentPage: newPage,
              }));
            }}
          />
        </>
      )}

      <CopyApprovalTeamModal
        open={!!copyTeam}
        team={copyTeam}
        companies={companies}
        createApprovalTeam={createApprovalTeam}
        onClose={() => setCopyTeam(null)}
        onCopied={handleCopied}
      />
      <DeleteApprovalTeamModal
        open={!!deleteTeam}
        team={deleteTeam}
        allTeams={approvalTeams.data}
        onClose={() => setDeleteTeam(null)}
        onDeleted={handleDeleted}
        deleteApprovalTeam={deleteApprovalTeam}
      />
    </div>
  );
}
